import * as React from 'react';
import { BlockerFunction, useBlocker } from 'react-router-dom';

const title = 'Sei sicuro di voler lasciare questa pagina?';
const handleRouteChange = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  event.returnValue = title;
  return title;
};

export function useFormGuard(
  formIsDirty: boolean,
  onCloseConfirmed: () => void,
) {
  const [requestedBlock, setRequestedBlock] = React.useState(false);
  const confirmedClose = React.useRef(false);

  const requestClose = () => {
    if (formIsDirty) {
      setRequestedBlock(true);
    } else {
      onCloseConfirmed();
    }
  };

  // Allow the submission navigation to the same route to go through
  const shouldBlock = React.useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => {
      return (
        !confirmedClose.current &&
        formIsDirty &&
        currentLocation.pathname !== nextLocation.pathname
      );
    },
    [formIsDirty, confirmedClose],
  );

  const blocker = useBlocker(shouldBlock);

  const stopNavigation = () => {
    blocker.reset?.();
    setRequestedBlock(false);
  };

  const proceedNavigation = () => {
    if (requestedBlock) {
      confirmedClose.current = true;
      setRequestedBlock(false);
      onCloseConfirmed();
    } else {
      blocker.proceed?.();
    }
  };

  const forceClose = () => {
    confirmedClose.current = true;
    setRequestedBlock(false);
    onCloseConfirmed();
  };

  const isBlocked = blocker.state === 'blocked';
  const isProceeding = blocker.state === 'proceeding';

  React.useEffect(() => {
    if (!formIsDirty) {
      window.removeEventListener('beforeunload', handleRouteChange);
      if (isBlocked) {
        blocker.reset?.();
      }
    } else {
      window.addEventListener('beforeunload', handleRouteChange);
    }

    return () => {
      window.removeEventListener('beforeunload', handleRouteChange);
    };
  }, [blocker, isBlocked, formIsDirty]);

  //console.log(blocker.state, new Date());

  return {
    stopNavigation,
    proceedNavigation,
    showAlert: isBlocked || isProceeding || requestedBlock,
    requestClose,
    forceClose,
  };
}
